import React, { useState, useEffect, useCallback } from 'react';
import { getUserCalls, formatDuration, formatCost, formatCallDate, getCallStatusColor, getCallSuccessText, downloadCallsCSV } from '../../../../../services/callService';
import Pagination from '../../../Pagination';
import CallDetailsModal from '../modals/CallDetailsModal';
import RecordingPlayer from '../shared/RecordingPlayer';
import { ResponsiveList, ResponsiveGrid, ResponsiveContainer } from '../../../../../components/common/ResponsiveLayout';
import MobileCard, { MobileCardContent } from '../../../../../components/common/MobileCard';
import { useMobile } from '../../../../../hooks/useMobile';
import { useAuth } from '../../../../../contexts/AuthContext';

// Effective identifier for a call row: Vapi rows have call_id (uuid); Retell
// rows have call_id NULL and use provider_call_id instead.
const getCallKey = (call) => call.call_id ?? call.provider_call_id;

const CallsTab = ({ userId, callStats, onRefresh, viewMode = 'admin' }) => {
  const { userRole } = useAuth();

  // State management
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCalls, setSelectedCalls] = useState(new Set());
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [successFilter, setSuccessFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('call_started_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state
  const [selectedCall, setSelectedCall] = useState(null);
  const [modalType, setModalType] = useState(null); // 'details', 'sa_report', 'coach_report', 'client_report', 'transcript'

  // Fetch calls with filters
  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const options = {
        search: searchTerm || null,
        status: statusFilter !== 'all' ? statusFilter : null,
        success: successFilter !== 'all' ? (successFilter === 'true') : null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        sortBy,
        sortOrder,
        page: currentPage,
        pageSize
      };

      const result = await getUserCalls(userId, options);

      if (result.error) {
        throw new Error(result.error);
      }

      setCalls(result.data || []);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
    } catch (err) {
      console.error('Error fetching calls:', err);
      setError(err.message || 'Failed to fetch calls');
    } finally {
      setLoading(false);
    }
  }, [userId, searchTerm, statusFilter, successFilter, dateFrom, dateTo, sortBy, sortOrder, currentPage, pageSize]);

  // Initial fetch and when dependencies change
  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, successFilter, dateFrom, dateTo]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Handle call selection
  const handleSelectCall = (callId) => {
    const newSelected = new Set(selectedCalls);
    if (newSelected.has(callId)) {
      newSelected.delete(callId);
    } else {
      newSelected.add(callId);
    }
    setSelectedCalls(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedCalls.size === calls.length) {
      setSelectedCalls(new Set());
    } else {
      setSelectedCalls(new Set(calls.map(call => getCallKey(call))));
    }
  };

  // Handle modal actions
  const handleViewDetails = (call) => {
    setSelectedCall(call);
    setModalType('details');
  };

  const handleViewSAReport = (call) => {
    setSelectedCall(call);
    setModalType('sa_report');
  };

  const handleViewCoachReport = (call) => {
    setSelectedCall(call);
    setModalType('coach_report');
  };

  const handleViewClientReport = (call) => {
    setSelectedCall(call);
    setModalType('client_report');
  };

  const handleViewSummary = (call) => {
    setSelectedCall(call);
    setModalType('summary');
  };

  const handleViewTranscript = (call) => {
    setSelectedCall(call);
    setModalType('transcript');
  };

  const closeModal = () => {
    setSelectedCall(null);
    setModalType(null);
  };

  // Handle export
  const handleExport = () => {
    try {
      const selectedCallsData = calls.filter(call => selectedCalls.has(getCallKey(call)));
      const dataToExport = selectedCallsData.length > 0 ? selectedCallsData : calls;
      
      downloadCallsCSV(dataToExport, `user_${userId}_calls.csv`);
    } catch (err) {
      setError('Failed to export calls: ' + err.message);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSuccessFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('call_started_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return '↕️';
    }
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Render call as mobile card
  const renderCallCard = (call, index) => {
    const getCallActions = (call) => {
      const actions = [];
      
      // Only Super Admins can view Details
      if (userRole === 'SUPER_ADMIN') {
        actions.push({
          label: 'Details',
          icon: '📋',
          onClick: () => handleViewDetails(call),
          variant: 'secondary'
        });
      }

      const hasNewReports = call.sa_report || call.coach_report || call.client_report;

      // If we have new report data, show the appropriate report tabs
      if (hasNewReports) {
        // Add report actions based on user role
        if (userRole === 'SUPER_ADMIN') {
          if (call.sa_report) {
            actions.push({
              label: 'SA Report',
              icon: '👑',
              onClick: () => handleViewSAReport(call),
              variant: 'secondary'
            });
          }
          if (call.coach_report) {
            actions.push({
              label: 'Coach Report',
              icon: '🎯',
              onClick: () => handleViewCoachReport(call),
              variant: 'secondary'
            });
          }
          if (call.client_report) {
            actions.push({
              label: 'Client Report',
              icon: '👤',
              onClick: () => handleViewClientReport(call),
              variant: 'secondary'
            });
          }
        } else if (userRole === 'COACH') {
          if (call.coach_report) {
            actions.push({
              label: 'Coach Report',
              icon: '🎯',
              onClick: () => handleViewCoachReport(call),
              variant: 'secondary'
            });
          }
          if (call.client_report) {
            actions.push({
              label: 'Client Report',
              icon: '👤',
              onClick: () => handleViewClientReport(call),
              variant: 'secondary'
            });
          }
        } else if (viewMode === 'client') {
          if (call.client_report) {
            actions.push({
              label: 'Client Report',
              icon: '👤',
              onClick: () => handleViewClientReport(call),
              variant: 'secondary'
            });
          }
        }
      } else if (call.report_summary) {
        // Fallback to Summary for older records
        actions.push({
          label: 'Summary',
          icon: '📝',
          onClick: () => handleViewSummary(call),
          variant: 'secondary'
        });
      }

      // Add transcript and recording actions
      if (call.report_transcript) {
        actions.push({
          label: 'Transcript',
          icon: '📜',
          onClick: () => handleViewTranscript(call),
          variant: 'secondary'
        });
      }

     // Only Super Admins can access recordings
      if (userRole === 'SUPER_ADMIN' && call.recording_url) {
        actions.push({
          label: 'Recording',
          icon: '🎵',
          // Recordings require an authenticated fetch; open the details modal
          // where the inline player retrieves a signed URL on demand.
          onClick: () => handleViewDetails(call),
          variant: 'secondary'
        });
      }

      return actions;
    };

    return (
      <MobileCard
        key={getCallKey(call)}
        onPress={userRole === 'SUPER_ADMIN' ? () => handleViewDetails(call) : undefined}
        actions={getCallActions(call)}
        showBorder={true}
        animated={true}
      >
        <MobileCardContent
          title={formatCallDate(call.call_started_at)}
          subtitle={call.assistant_name || 'Unknown Assistant'}
          badge={
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCallStatusColor(call.call_status, call.success_evaluation)}`}>
              {getCallSuccessText(call.success_evaluation, call.ended_reason)}
            </span>
          }
          metadata={[
            {
              icon: '⏱️',
              label: formatDuration(call.duration_minutes)
            },
            ...(userRole === 'SUPER_ADMIN' ? [{
              icon: '💰',
              label: formatCost(call.total_cost)
            }] : [])
         ]}
          actions={getCallActions(call)}
        >
          <div className="mt-3">
            <input
              type="checkbox"
              checked={selectedCalls.has(getCallKey(call))}
              onChange={() => handleSelectCall(getCallKey(call))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-600">Select</label>
          </div>
        </MobileCardContent>
      </MobileCard>
    );
  };

  // Render call as table row
  const renderCallRow = (call, index) => (
    <tr key={getCallKey(call)} className="hover:bg-gray-50">
      <td className="px-4 py-4">
        <input
          type="checkbox"
          checked={selectedCalls.has(getCallKey(call))}
          onChange={() => handleSelectCall(getCallKey(call))}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
        {formatCallDate(call.call_started_at)}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
        {formatDuration(call.duration_minutes)}
      </td>
      {viewMode !== 'client' && (
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {call.assistant_name || 'Unknown'}
        </td>
      )}
      {viewMode !== 'client' && (
        <td className="px-4 py-4 whitespace-nowrap">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCallStatusColor(call.call_status, call.success_evaluation)}`}>
            {getCallSuccessText(call.success_evaluation, call.ended_reason)}
          </span>
        </td>
      )}
      {userRole === 'SUPER_ADMIN' && (
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
        {formatCost(call.total_cost)}
      </td>
      )}
      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
        {/* Details button - only for Super Admin */}
        {userRole === 'SUPER_ADMIN' && (
        <button
          onClick={() => handleViewDetails(call)}
          className="text-indigo-600 hover:text-indigo-900"
          title="View Details"
        >
          📋
        </button>
        )}
        {/* Report buttons based on user role */}
        {userRole === 'SUPER_ADMIN' && call.sa_report && (
          <button
            onClick={() => handleViewSAReport(call)}
            className="text-yellow-600 hover:text-yellow-900"
            title="View SA Report"
          >
            👑
          </button>
        )}
        
        {(userRole === 'SUPER_ADMIN' || userRole === 'COACH') && call.coach_report && (
          <button
            onClick={() => handleViewCoachReport(call)}
            className="text-blue-600 hover:text-blue-900"
            title="View Coach Report"
          >
            🎯
          </button>
        )}
        
        {(userRole === 'SUPER_ADMIN' || userRole === 'COACH' || viewMode === 'client') && call.client_report && (
          <button
            onClick={() => handleViewClientReport(call)}
            className="text-green-600 hover:text-green-900"
            title="View Client Report"
          >
            👤
          </button>
        )}

        {call.report_transcript && (
          <button
            onClick={() => handleViewTranscript(call)}
            className="text-gray-600 hover:text-gray-900"
            title="View Transcript"
          >
            📜
          </button>
        )}
        {/* Only Super Admins can access recordings */}
        {userRole === 'SUPER_ADMIN' && call.recording_url && (
          <RecordingPlayer callId={getCallKey(call)} variant="icon" />
        )}
      </td>
    </tr>
  );

  if (loading && calls.length === 0) {
    return (
      <div className="space-y-4">
        {/* Skeleton loading */}
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Contact History ({totalCount} total)
          </h3>
          {viewMode !== 'client' && (
            <p className="text-sm text-gray-600">
              View and manage all contacts for this user
            </p>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {selectedCalls.size > 0 && (
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              📊 Export Selected ({selectedCalls.size})
            </button>
          )}
          
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Search */}
          <div className="xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search contacts..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>

          {/* Status Filter — hidden for clients */}
          {viewMode !== 'client' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="in-progress">In Progress</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          )}

          {/* Success Filter — hidden for clients */}
          {viewMode !== 'client' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Success</label>
              <select
                value={successFilter}
                onChange={(e) => setSuccessFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="all">All Results</option>
                <option value="true">Successful</option>
                <option value="false">Failed</option>
              </select>
            </div>
          )}

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Clear Filters
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            {calls.length} of {totalCount} contacts
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Calls List */}
      <ResponsiveList
        items={calls}
        loading={loading}
        renderCard={renderCallCard}
        renderTableRow={renderCallRow}
        tableHeaders={[
          '',
          'Date/Time',
          'Duration',
          ...(viewMode !== 'client' ? ['Assistant', 'Status'] : []),
          ...(userRole === 'SUPER_ADMIN' ? ['Cost'] : []),
          'Actions'
        ]}
        empty={
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4">📞</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Contacts Found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' || successFilter !== 'all' || dateFrom || dateTo
                ? 'No contacts match your current filters.'
                : 'This user hasn\'t made any contacts yet.'}
            </p>
          </div>
        }
      />

      {/* Pagination */}
      {calls.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Modals */}
      {selectedCall && modalType && (
        <CallDetailsModal
          call={selectedCall}
          type={modalType}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default CallsTab;